/**
 * Payment strategy layer (Phase 4).
 *
 * A registry of payment processors keyed by PAYMENT_BEHAVIOR. Each processor
 * exposes a pure `normalize(raw, ctx?)` that maps a raw device/helper response
 * into the shared {@link makePaymentResult} shape, so the checkout `pay()`
 * orchestration is processor-agnostic.
 *
 * Device I/O and side effects (modals, store writes, the Phase-1 payment lock,
 * post-tip fully-paid recheck, retry idempotency) deliberately remain in the
 * checkout component; this layer only owns processor selection and response
 * normalization.
 */
import { PAYMENT_BEHAVIOR } from "@/helpers/order/payment/behavior";
import { makePaymentResult } from "./paymentResult";
import { cashProcessor } from "./processors/cashProcessor";
import { giftCardProcessor } from "./processors/giftCardProcessor";
import { nabancardProcessor } from "./processors/nabancardProcessor";
import { dejavooProcessor } from "./processors/dejavooProcessor";
import { windcaveProcessor } from "./processors/windcaveProcessor";

const PROCESSORS = [
  cashProcessor,
  giftCardProcessor,
  nabancardProcessor,
  dejavooProcessor,
  windcaveProcessor,
];

const BY_BEHAVIOR = PROCESSORS.reduce((map, processor) => {
  (processor.behaviors || []).forEach((behavior) => {
    map[behavior] = processor;
  });
  return map;
}, {});

/**
 * Resolve the processor for a payment behavior. Falls back to the cash
 * processor (the legacy default for non-integration behaviors).
 */
export function getPaymentProcessor(behavior) {
  return BY_BEHAVIOR[behavior] || cashProcessor;
}

export {
  makePaymentResult,
  PAYMENT_BEHAVIOR,
  cashProcessor,
  giftCardProcessor,
  nabancardProcessor,
  dejavooProcessor,
  windcaveProcessor,
};
